import { secureHttp } from './api/secureHttp';
import { beHttp } from './api/beHttp';

export interface PsychologistWorkSchedule {
  id: string;
  created_at: string;
  updated_at: string;
  psychologist_id: string;
  weekday: number;
  start_time: string;
  end_time: string;
  active: boolean;
}

export interface PsychologistWorkSchedulesResponse {
  results: PsychologistWorkSchedule[];
  metadata?: {
    total?: number;
    pages?: number;
    current_page?: number;
    per_page?: number;
    has_next_page?: boolean;
    has_prev_page?: boolean;
  };
}

export interface PsychologistWorkSchedulesQuery extends Record<string, unknown> {
  active?: boolean;
  weekday?: number;
  skip?: number;
  take?: number;
  page?: number;
  per_page?: number;
}

export interface CreateWorkSchedulePayload {
  weekday: number;
  start_time: string;
  end_time: string;
  active?: boolean;
  appointment_types?: string[];
}

export interface UpdateWorkSchedulePayload {
  weekday?: number;
  start_time?: string;
  end_time?: string;
  active?: boolean;
}

export interface CreateDirectSessionPayload {
  process_id: string;
  date: string;
  scheduled_by: string;
  notes?: string;
  appointment_type?: string;
}

export interface DirectSessionResponse {
  id: string;
  date: string;
  process_id: string;
  scheduled_by: string;
  notes?: string;
  status?: string;
  created_at?: string;
  appointment_type?: string;
}

export interface GenerateSlotsPayload {
  start_date: string;
  end_date: string;
  psychologist_ids: string[];
  all_psychologists?: boolean;
  appointment_types_by_psychologist?: Record<string, string[]>;
}

export interface GenerateSlotsResponse {
  total_slots_generated: number;
  psychologists_processed: number;
  slots_by_psychologist: Record<string, number>;
}

export default {
  async psychologistWorkSchedules(params?: PsychologistWorkSchedulesQuery): Promise<PsychologistWorkSchedulesResponse> {
    return await beHttp.get<PsychologistWorkSchedulesResponse>(
      '/api/psychologist/calendar/work-schedules',
      params,
    );
  },

  async patientsInformation(params?: { page?: number; per_page?: number; status?: string; therapy_type?: string }): Promise<{
    results: Array<{
      id: string;
      name: string | null;
      surname: string | null;
      therapy_type?: string | null;
      last_session_date?: string | null;
      next_session_date?: string | null;
    }>;
    metadata?: {
      total?: number;
      pages?: number;
      current_page?: number;
      per_page?: number;
      has_next_page?: boolean;
      has_prev_page?: boolean;
    };
  }> {
    const page = params?.page ?? 1;
    const per_page = params?.per_page ?? 10;
    const status = params?.status;
    const therapy_type = params?.therapy_type;
    const query: Record<string, string | number> = { page, per_page };
    if (status) query.status = status;
    if (therapy_type) query.therapy_type = therapy_type;
    return await beHttp.get(`/api/psychologist/patients/process-sessions-data`, query);
  },

  async createPsychologistWorkSchedule(data: CreateWorkSchedulePayload): Promise<PsychologistWorkSchedule> {
    return await beHttp.post<PsychologistWorkSchedule>(
      '/api/psychologist/calendar/work-schedules',
      data,
    );
  },

  async updatePsychologistWorkSchedule(id: string, data: UpdateWorkSchedulePayload): Promise<PsychologistWorkSchedule> {
    return await beHttp.put<PsychologistWorkSchedule>(
      `/api/psychologist/calendar/work-schedules/${id}`,
      data,
    );
  },

  async deletePsychologistWorkSchedule(id: string): Promise<void> {
    return await beHttp.delete<void>(
      `/api/psychologist/calendar/work-schedules/${id}`,
    );
  },

  async createDirectSession(data: CreateDirectSessionPayload): Promise<DirectSessionResponse> {
    // createDirectSession es CORE — sigue en api-core (deprecated, preferir scheduleSession).
    return await secureHttp.post<DirectSessionResponse, CreateDirectSessionPayload>(
      '/v1/psi-platform/session/direct',
      data,
    );
  },

  async generateSlotsFromWorkSchedules(data: GenerateSlotsPayload): Promise<GenerateSlotsResponse> {
    return await beHttp.post<GenerateSlotsResponse>(
      '/api/psychologist/calendar/work-schedules/generate-slots',
      data,
    );
  },
};

